import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { connectGithub, disconnectGithub, getGithubStatus, listRepos } from "../lib/api";
import ProcessingScreen from "../components/ProcessingScreen";
import {
  ArrowRight,
  Github,
  Lock,
  Loader2,
  Search,
  CheckCircle2,
  Activity,
  Clock,
  History,
  FileCode2,
} from "lucide-react";
import { toast } from "sonner";
import { fmtRelative } from "../lib/format";
import { track } from "../lib/track";
import { WaitlistTeaser } from "../components/Waitlist";

export default function Onboarding() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [repos, setRepos] = useState([]);
  const [reposLoading, setReposLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [creatingId, setCreatingId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let active = true;
    getGithubStatus()
      .then((s) => {
        if (active) setStatus(s);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const [tokenInvalid, setTokenInvalid] = useState(false);

  useEffect(() => {
    if (!status?.connected) return;
    setReposLoading(true);
    setTokenInvalid(false);
    listRepos()
      .then((r) => {
        setRepos(r);
        setTokenInvalid(false);
      })
      .catch((e) => {
        const detail = e?.response?.data?.detail || "";
        if (e?.response?.status === 401 || /401|invalid/i.test(detail)) {
          setTokenInvalid(true);
          setRepos([]);
        } else {
          toast.error("Failed to load repos");
        }
      })
      .finally(() => setReposLoading(false));
  }, [status?.connected]);

  const onConnect = async () => {
    if (!token.trim()) {
      toast.error("Paste your GitHub token");
      return;
    }
    setConnecting(true);
    try {
      const s = await connectGithub(token.trim());
      setStatus(s);
      setToken("");
      track("PAT Connected", { props: { login: s.github_login } });
      toast.success(`Connected as ${s.github_login}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid token");
    } finally {
      setConnecting(false);
    }
  };

  const onCreate = (full_name) => {
    // Switch to processing screen — actual API call is run inside ProcessingScreen
    track("Project Started", { props: { repo: full_name } });
    setCreatingId(full_name);
  };

  const filtered = repos.filter((r) =>
    `${r.full_name} ${r.description || ""}`.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="min-h-screen flex flex-col bg-white relative">
      {creatingId && (
        <ProcessingScreen
          fullName={creatingId}
          onComplete={(p) => {
            toast.success(`${p.name} added to Trace`);
            navigate(`/project/${p.id}?welcome=1`);
          }}
          onError={(msg) => {
            toast.error(msg);
            setCreatingId(null);
          }}
        />
      )}
      {/* faint topo background */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-[0.4]"
        style={{
          backgroundImage:
            "url('https://static.prod-images.emergentagent.com/jobs/d51a684d-6a21-4b39-8b43-c02965a25ab0/images/2f2e3d13da85b38ad9d2ad0dbbc6e45bf434e189b48350d06f58b02836e01f45.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      {/* Top nav */}
      <header className="relative z-10 flex items-center justify-between px-6 sm:px-8 py-4">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 hover:opacity-80 transition"
          data-testid="brand-logo"
          aria-label="Back to Trace home"
        >
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
            <Activity size={14} className="text-white" strokeWidth={2.4} />
          </div>
          <span className="text-[15px] font-semibold tracking-tight text-[#111827]">Trace</span>
        </button>
        <nav className="flex items-center gap-5 text-xs text-[#6b7280]">
          {status?.connected && (
            <button
              onClick={async () => {
                try {
                  await disconnectGithub();
                  setStatus({ connected: false });
                  setRepos([]);
                  toast.success("Disconnected. Paste a new token to continue.");
                } catch {
                  toast.error("Failed to disconnect");
                }
              }}
              data-testid="nav-disconnect"
              className="hover:text-[#111827] transition"
            >
              Use a different token
            </button>
          )}
          <a
            href="https://github.com/settings/tokens?type=beta"
            target="_blank"
            rel="noreferrer"
            className="hover:text-[#111827] transition inline-flex items-center gap-1.5"
            data-testid="nav-github"
          >
            <Github size={13} />
            GitHub
          </a>
        </nav>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-6 sm:px-8 py-6">
        {!status?.connected ? (
          <div className="w-full max-w-[560px] flex flex-col items-center text-center trace-fade-in">
            {/* Hero */}
            <h1
              data-testid="hero-headline"
              className="text-[34px] sm:text-[44px] font-semibold tracking-tight leading-[1.05] text-[#111827]"
            >
              Never lose context on{" "}
              <span className="gradient-text">your code</span> again.
            </h1>
            <p className="mt-4 text-[15px] text-[#6b7280] leading-relaxed max-w-[480px]">
              Trace remembers what you were doing — so you can instantly pick up where you left off.
            </p>

            {/* Resume Session preview card (the magic) */}
            <PreviewCard />

            {/* Connect box */}
            <ConnectBox
              token={token}
              setToken={setToken}
              connecting={connecting || loading}
              onConnect={onConnect}
            />

            <p className="mt-5 text-[11px] text-[#9ca3af] max-w-[440px] mx-auto leading-relaxed">
              Local-first. Your token stays private. We only read repo activity.
            </p>

            <div className="mt-4 flex justify-center">
              <WaitlistTeaser />
            </div>
          </div>
        ) : (
          <RepoPicker
            status={status}
            repos={filtered}
            allRepos={repos}
            reposLoading={reposLoading}
            filter={filter}
            setFilter={setFilter}
            creatingId={creatingId}
            onCreate={onCreate}
            tokenInvalid={tokenInvalid}
            onReconnect={async () => {
              try {
                await disconnectGithub();
                setStatus({ connected: false });
                setRepos([]);
                setTokenInvalid(false);
              } catch {}
            }}
          />
        )}
      </main>

      {/* Tiny footer */}
      <footer className="relative z-10 flex items-center justify-between px-6 sm:px-8 py-4 text-[11px] text-[#9ca3af]">
        <span>Trace · Project memory for builders</span>
        <div className="flex items-center gap-4">
          <span>safe & private</span>
          <a
            href="https://github.com/settings/tokens?type=beta"
            target="_blank"
            rel="noreferrer"
            className="hover:text-[#111827] transition"
          >
            Generate token →
          </a>
        </div>
      </footer>
    </div>
  );
}

/* ---------------- Preview card ---------------- */
const PREVIEW_EXAMPLES = [
  {
    project: "Payment integration",
    last_active: "2 days ago",
    duration: "1h 12m",
    files: ["checkout.tsx", "payment_api.js", "webhook_handler.py"],
    next: "Test webhook response and failure states.",
  },
  {
    project: "Auth system",
    last_active: "yesterday",
    duration: "48m",
    files: ["login.tsx", "auth_service.py", "session.ts"],
    next: "Verify token refresh on expired sessions.",
  },
  {
    project: "Dashboard UI",
    last_active: "3 hours ago",
    duration: "2h 04m",
    files: ["Sidebar.jsx", "Charts.tsx", "useMetrics.ts"],
    next: "Wire the empty state for first-time users.",
  },
];

function PreviewCard() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % PREVIEW_EXAMPLES.length), 4200);
    return () => clearInterval(t);
  }, [paused]);

  const ex = PREVIEW_EXAMPLES[idx];

  return (
    <div
      data-testid="resume-preview"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      className="mt-9 w-full rounded-2xl bg-white border border-[#e5e7eb] p-6 sm:p-7 text-left shadow-[0_10px_30px_rgba(17,24,39,0.05)] hover:-translate-y-0.5 transition-transform duration-300"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#6b7280]">
          <History size={13} className="text-indigo-500" />
          <span className="font-semibold text-[#111827] tracking-[0.18em]">Resume Session</span>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[#6b7280]">
          <span className="flex items-center gap-1.5">
            <Clock size={11} />
            Last active: {ex.last_active}
          </span>
          <span className="hidden sm:inline w-1 h-1 rounded-full bg-[#d1d5db]" />
          <span className="hidden sm:inline">Last session: {ex.duration}</span>
        </div>
      </div>

      {/* Hero — most prominent */}
      <div key={idx} className="trace-fade-in">
        <div className="mt-5">
          <Label>You were in the middle of building</Label>
          <h3
            data-testid="preview-project"
            className="mt-1.5 text-[24px] sm:text-[28px] font-semibold tracking-tight leading-tight text-[#111827]"
          >
            <span className="gradient-text">{ex.project}</span>.
          </h3>
        </div>

        <div className="mt-5 grid sm:grid-cols-[180px_1fr] gap-5 items-start">
          {/* Smaller secondary list */}
          <div>
            <Label>You worked on</Label>
            <ul className="mt-2 space-y-1">
              {ex.files.map((f) => (
                <li
                  key={f}
                  className="flex items-center gap-2 font-mono text-[11.5px] text-[#374151]"
                >
                  <FileCode2 size={10} className="text-[#9ca3af]" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
          {/* Next step */}
          <div>
            <Label>Next step</Label>
            <p className="mt-1.5 text-[14px] text-[#111827] flex items-start gap-1.5 leading-relaxed">
              <ArrowRight size={13} className="text-indigo-500 mt-1 shrink-0" />
              <span>{ex.next}</span>
            </p>
            <p className="mt-2 text-[12px] text-[#9ca3af]">Jump back in instantly.</p>
          </div>
        </div>
      </div>

      {/* Rotation dots */}
      <div className="mt-5 flex items-center justify-center gap-1.5" data-testid="preview-dots">
        {PREVIEW_EXAMPLES.map((_, i) => (
          <button
            key={i}
            aria-label={`Show example ${i + 1}`}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === idx ? "w-6 bg-gradient-to-r from-blue-500 to-indigo-500" : "w-1.5 bg-[#e5e7eb] hover:bg-[#d1d5db]"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

function Label({ children }) {
  return (
    <span className="text-[10px] uppercase tracking-[0.15em] text-[#6b7280] font-medium">
      {children}
    </span>
  );
}

/* ---------------- Connect box ---------------- */
function ConnectBox({ token, setToken, connecting, onConnect }) {
  return (
    <div
      data-testid="github-connect-card"
      className="mt-5 w-full rounded-2xl bg-[#f7f8fa] border border-[#e5e7eb] p-5 sm:p-6 text-left"
    >
      <div className="flex items-center gap-2 text-sm">
        <Github size={15} />
        <span className="font-semibold text-[#111827]">Connect GitHub</span>
      </div>
      <p className="text-[12px] text-[#6b7280] mt-1.5 leading-relaxed">
        Paste your GitHub token (safe &amp; private). We only read your repo activity.
      </p>

      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <div className="flex-1 relative">
          <Lock
            size={13}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9ca3af]"
          />
          <input
            data-testid="pat-input"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="ghp_..."
            className="w-full font-mono text-sm pl-9 pr-3 py-2.5 rounded-lg bg-white border border-[#e5e7eb] outline-none focus:border-indigo-400 transition"
          />
        </div>
        <button
          data-testid="pat-connect-btn"
          onClick={onConnect}
          disabled={connecting}
          className="group px-6 py-3 rounded-lg text-white text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(99,102,241,0.4)] transition-all duration-300 disabled:opacity-60 disabled:hover:translate-y-0 inline-flex items-center justify-center gap-2"
        >
          {connecting ? (
            <Loader2 size={14} className="animate-spin" />
          ) : null}
          {connecting ? "Starting" : "Start Tracing"}
          {!connecting && (
            <ArrowRight
              size={14}
              className="transition-transform duration-300 group-hover:translate-x-0.5"
            />
          )}
        </button>
      </div>
    </div>
  );
}

/* ---------------- Repo picker (after connect) ---------------- */
function RepoPicker({
  status,
  repos,
  allRepos,
  reposLoading,
  filter,
  setFilter,
  creatingId,
  onCreate,
  tokenInvalid = false,
  onReconnect,
}) {
  const [manual, setManual] = useState("");
  const validManual = /^[\w.-]+\/[\w.-]+$/.test(manual.trim());
  const noRepos = !reposLoading && allRepos.length === 0 && !tokenInvalid;

  return (
    <div className="w-full max-w-[640px] trace-fade-in">
      <div className="text-center">
        <h1 className="text-[28px] sm:text-[34px] font-semibold tracking-tight text-[#111827]">
          One last step.{" "}
          <span className="gradient-text">Pick a repo to remember.</span>
        </h1>
        <p className="mt-3 text-[14px] text-[#6b7280]">
          Trace will quietly study its commits and build your project memory.
        </p>
      </div>

      <div
        className={`mt-6 trace-card p-4 flex items-center justify-between ${tokenInvalid ? "border-rose-200 bg-rose-50/40" : ""}`}
        data-testid="connected-card"
      >
        <div className="flex items-center gap-3">
          {status.avatar_url ? (
            <img
              src={status.avatar_url}
              alt=""
              className="w-9 h-9 rounded-full border border-[#e5e7eb]"
            />
          ) : (
            <Github size={28} />
          )}
          <div>
            <div className="text-sm text-[#111827] flex items-center gap-1.5">
              {tokenInvalid ? (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                  <span>Token expired or revoked</span>
                </>
              ) : (
                <>
                  <CheckCircle2 size={13} className="text-emerald-500" />
                  Connected as <span className="font-semibold">{status.github_login}</span>
                </>
              )}
            </div>
            <div className="text-[11px] text-[#6b7280]">
              {tokenInvalid
                ? "Trace can't read GitHub with this token anymore."
                : `${allRepos.length} ${allRepos.length === 1 ? "repo" : "repos"} available`}
            </div>
          </div>
        </div>
        {tokenInvalid && (
          <button
            data-testid="reconnect-btn"
            onClick={onReconnect}
            className="px-3 py-1.5 rounded-lg text-white text-xs font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-95 transition"
          >
            Paste a new token
          </button>
        )}
      </div>

      {/* Manual add — works even if list is empty (fine-grained PAT case) */}
      <div
        className="mt-4 rounded-2xl bg-[#f7f8fa] border border-[#e5e7eb] p-4"
        data-testid="manual-add-card"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-[#111827]">Add by name</div>
            <div className="text-[11px] text-[#6b7280] mt-0.5">
              Paste any repo your token can read, e.g.{" "}
              <span className="font-mono">{status.github_login}/my-app</span>
            </div>
          </div>
        </div>
        <div className="mt-3 flex gap-2">
          <input
            data-testid="manual-repo-input"
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && validManual) onCreate(manual.trim());
            }}
            placeholder="owner/repo"
            className="flex-1 font-mono text-sm pl-3 pr-3 py-2.5 rounded-lg bg-white border border-[#e5e7eb] outline-none focus:border-indigo-400 transition"
          />
          <button
            data-testid="manual-add-btn"
            disabled={!validManual || creatingId === manual.trim()}
            onClick={() => onCreate(manual.trim())}
            className="px-4 py-2.5 rounded-lg text-white text-sm font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-95 transition disabled:opacity-50 disabled:hover:opacity-50 inline-flex items-center gap-1.5"
          >
            {creatingId === manual.trim() ? (
              <Loader2 size={13} className="animate-spin" />
            ) : (
              <ArrowRight size={13} />
            )}
            Start Tracing
          </button>
        </div>
      </div>

      {noRepos ? (
        <div
          className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-[12px] text-[#92400e] leading-relaxed"
          data-testid="no-repos-hint"
        >
          <div className="font-semibold text-[#7c2d12] mb-1">No repos visible to this token</div>
          Fine-grained tokens only show repos you explicitly granted access to. Either grant
          repository access in your{" "}
          <a
            href="https://github.com/settings/tokens?type=beta"
            target="_blank"
            rel="noreferrer"
            className="underline hover:text-[#7c2d12]"
          >
            token settings
          </a>{" "}
          (Repository access → Only select repositories → pick one), or just type the repo above
          as <span className="font-mono">owner/repo</span>.
        </div>
      ) : (
        <>
          <div className="relative mt-4">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9ca3af]"
            />
            <input
              data-testid="repo-filter-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter repos..."
              className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-[#f7f8fa] border border-[#e5e7eb] outline-none focus:border-indigo-400 transition text-sm"
            />
          </div>

          <ul
            className="mt-3 space-y-2 max-h-[38vh] overflow-y-auto pr-1"
            data-testid="repo-list"
          >
            {reposLoading ? (
              <li className="text-sm text-[#6b7280] flex items-center gap-2 px-1">
                <Loader2 size={14} className="animate-spin" /> Loading repos…
              </li>
            ) : repos.length === 0 ? (
              <li className="text-sm text-[#6b7280] px-1">No repos match.</li>
            ) : (
              repos.map((r) => (
                <li
                  key={r.full_name}
                  className="trace-card p-3.5 flex items-start justify-between gap-3 hover:-translate-y-0.5 transition-transform duration-300"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#111827] flex items-center gap-2">
                      <span className="truncate">{r.full_name}</span>
                      {r.private && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white border border-[#e5e7eb] text-[#6b7280]">
                          private
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-[#6b7280] mt-0.5 line-clamp-1">
                      {r.description || "—"}
                    </div>
                    <div className="text-[10px] text-[#9ca3af] mt-1 flex items-center gap-3">
                      <span>{r.language || "—"}</span>
                      <span>updated {fmtRelative(r.updated_at)}</span>
                      <span>★ {r.stars}</span>
                    </div>
                  </div>
                  <button
                    data-testid={`add-repo-${r.full_name}`}
                    disabled={creatingId === r.full_name}
                    onClick={() => onCreate(r.full_name)}
                    className="shrink-0 px-3 py-1.5 rounded-lg text-white text-xs bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-95 transition disabled:opacity-60 inline-flex items-center gap-1.5"
                  >
                    {creatingId === r.full_name ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <ArrowRight size={12} />
                    )}
                    Start Tracing
                  </button>
                </li>
              ))
            )}
          </ul>
        </>
      )}
    </div>
  );
}
